import React, { useState, useEffect } from 'react';
import { Card, Input, Button, Skeleton } from '../components/ui';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useTranslation } from '../hooks/useTranslation';
import { LockReset, CheckCircle, Error as ErrorIcon } from '../mui-icons';
import './ResetPasswordPage.css';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const token = searchParams.get('token');

  const [tokenValid, setTokenValid] = useState(null); // null = validando, true = válido, false = inválido
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [tokenError, setTokenError] = useState('');

  // Detect mobile and redirect to deeplink
  useEffect(() => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    if (isMobile && token) {
      const deeplink = `personalnutri://reset-password?token=${token}`;
      window.location.href = deeplink;
      
      // Fallback: continue on web after 2 seconds if app doesn't open
      setTimeout(() => {
        // Continue on web flow (does nothing)
      }, 2000);
    }
  }, [token]);

  // Validate token on load
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenValid(false);
        setTokenError(t('resetPassword.error.tokenNotProvided'));
        return;
      }

      try {
        const response = await fetch(
          `https://api.personalnutri.app/user/reset-password/validate/${token}`
        );
        const data = await response.json();

        if (response.ok && data.valid) {
          setTokenValid(true);
          setEmail(data.email || '');
        } else {
          setTokenValid(false);
          setTokenError(data.message || t('resetPassword.error.tokenInvalid'));
        }
      } catch (err) {
        setTokenValid(false);
        setTokenError(t('resetPassword.error.validationError'));
      }
    };

    validateToken();
  }, [token]);

  // Password validation
  const validatePassword = (password) => {
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return regex.test(password);
  };

  const getPasswordStrength = (password) => {
    if (password.length === 0) return { level: 0, text: '', color: '' };
    if (password.length < 8) return { level: 1, text: t('resetPassword.form.strength.weak'), color: '#F44336' };
    
    let strength = 0;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[@$!%*?&]/.test(password)) strength++;
    
    if (strength <= 2) return { level: 1, text: t('resetPassword.form.strength.weak'), color: '#F44336' };
    if (strength === 3) return { level: 2, text: t('resetPassword.form.strength.medium'), color: '#FF9800' };
    return { level: 3, text: t('resetPassword.form.strength.strong'), color: '#4CAF50' };
  };

  const passwordStrength = getPasswordStrength(newPassword);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validations
    if (!newPassword || !confirmPassword) {
      setError(t('resetPassword.form.validation.fillAll'));
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('resetPassword.form.validation.passwordsMatch'));
      setLoading(false);
      return;
    }

    if (!validatePassword(newPassword)) {
      setError(t('resetPassword.form.validation.passwordRequirements'));
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('https://api.personalnutri.app/user/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          new_password: newPassword,
          confirm_password: confirmPassword,
        }),
      });

      const data = await response.json();

      if (response.ok && data.status === 'success') {
        setSuccess(true);
        // Redirect to home after 5 seconds
        setTimeout(() => {
          navigate('/?message=password-reset-success');
        }, 5000);
      } else {
        setError(data.message || t('resetPassword.form.validation.resetError'));
      }
    } catch (err) {
      setError(t('resetPassword.form.validation.connectionError'));
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (tokenValid === null) {
    return (
      <div className="reset-password-page">
        <Header />
        <div className="reset-password-container surface-app">
          <Card variant="auth">
            <Skeleton width="70%" height={24} borderRadius={4} style={{ margin: '0 auto 16px' }} />
            <Skeleton width="100%" height={48} borderRadius={16} style={{ marginBottom: 12 }} />
            <Skeleton width="100%" height={48} borderRadius={16} />
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  // Invalid token
  if (tokenValid === false) {
    return (
      <div className="reset-password-page">
        <Header />
        <div className="reset-password-container">
          <div className="error-card">
            <div className="error-icon-wrapper">
              <ErrorIcon sx={{ fontSize: 80 }} />
            </div>
            <h1 className="error-title">{t('resetPassword.error.title')}</h1>
            <p className="error-message">{tokenError}</p>
            <div className="error-info">
              <p>{t('resetPassword.error.causes')}</p>
              <ul>
                <li>{t('resetPassword.error.expired')}</li>
                <li>{t('resetPassword.error.used')}</li>
                <li>{t('resetPassword.error.incorrect')}</li>
              </ul>
            </div>
            <Button variant="primary" onClick={() => navigate('/forgot-password')}>
              {t('resetPassword.error.requestNew')}
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Success
  if (success) {
    return (
      <div className="reset-password-page">
        <Header />
        <div className="reset-password-container">
          <div className="success-card">
            <div className="success-icon-wrapper">
              <CheckCircle sx={{ fontSize: 80 }} />
            </div>
            <h1 className="success-title">{t('resetPassword.success.title')}</h1>
            <p className="success-message">
              {t('resetPassword.success.message')}
            </p>
            <div className="success-info">
              <p className="redirect-info">{t('resetPassword.success.redirect')}</p>
            </div>
            <Button variant="primary" onClick={() => navigate('/')}>
              {t('resetPassword.success.goHome')}
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Form
  return (
    <div className="reset-password-page">
      <Header />
      
      <div className="reset-password-container surface-app">
        <div className="reset-password-content">
          <Card variant="auth" className="reset-password-card">
            <div className="card-icon">
              <LockReset sx={{ fontSize: 56 }} />
            </div>
            
            <h1 className="card-title">{t('resetPassword.form.title')}</h1>
            <p className="card-subtitle">
              {t('resetPassword.form.emailLabel')} <strong>{email}</strong>
            </p>

            <form onSubmit={handleSubmit} className="reset-password-form">
              {/* Nova Senha */}
              <Input
                id="newPassword"
                label={t('resetPassword.form.newPassword.label')}
                type={showPassword ? 'text' : 'password'}
                icon="lock"
                placeholder={t('resetPassword.form.newPassword.placeholder')}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={loading}
                autoFocus
                showPasswordToggle
                passwordVisible={showPassword}
                onTogglePassword={() => setShowPassword(!showPassword)}
              />
                
                {/* Indicador de força da senha */}
                {newPassword && (
                  <div className="password-strength">
                    <div className="strength-bars">
                      <div 
                        className={`strength-bar ${passwordStrength.level >= 1 ? 'active' : ''}`}
                        style={{ backgroundColor: passwordStrength.level >= 1 ? passwordStrength.color : '' }}
                      ></div>
                      <div 
                        className={`strength-bar ${passwordStrength.level >= 2 ? 'active' : ''}`}
                        style={{ backgroundColor: passwordStrength.level >= 2 ? passwordStrength.color : '' }}
                      ></div>
                      <div 
                        className={`strength-bar ${passwordStrength.level >= 3 ? 'active' : ''}`}
                        style={{ backgroundColor: passwordStrength.level >= 3 ? passwordStrength.color : '' }}
                      ></div>
                    </div>
                    <span 
                      className="strength-text"
                      style={{ color: passwordStrength.color }}
                    >
                      {passwordStrength.text}
                    </span>
                  </div>
                )}

              {/* Confirmar Senha */}
              <Input
                id="confirmPassword"
                label={t('resetPassword.form.confirmPassword.label')}
                type={showConfirmPassword ? 'text' : 'password'}
                icon="lock"
                placeholder={t('resetPassword.form.confirmPassword.placeholder')}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
                showPasswordToggle
                passwordVisible={showConfirmPassword}
                onTogglePassword={() => setShowConfirmPassword(!showConfirmPassword)}
              />

              {/* Requisitos de senha */}
              <div className="password-requirements">
                <p className="requirements-title">{t('resetPassword.form.requirements.title')}</p>
                <ul className="requirements-list">
                  <li className={newPassword.length >= 8 ? 'valid' : ''}>
                    {t('resetPassword.form.requirements.minLength')}
                  </li>
                  <li className={/[A-Z]/.test(newPassword) ? 'valid' : ''}>
                    {t('resetPassword.form.requirements.uppercase')}
                  </li>
                  <li className={/[a-z]/.test(newPassword) ? 'valid' : ''}>
                    {t('resetPassword.form.requirements.lowercase')}
                  </li>
                  <li className={/\d/.test(newPassword) ? 'valid' : ''}>
                    {t('resetPassword.form.requirements.number')}
                  </li>
                  <li className={/[@$!%*?&]/.test(newPassword) ? 'valid' : ''}>
                    {t('resetPassword.form.requirements.special')}
                  </li>
                </ul>
              </div>

              {error && (
                <div className="error-message">
                  <ErrorIcon />
                  <span>{error}</span>
                </div>
              )}

              <Button type="submit" variant="primary" loading={loading} block>
                {t('resetPassword.form.submit.default')}
              </Button>
            </form>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ResetPasswordPage;

